// Belge önizleme penceresindeki genel onay/red paneli; kayıt işlemi çağırana bırakılır
import { useState } from 'react';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Checkbox } from '../ui/checkbox';
import { CheckCircle2, XCircle, MinusCircle } from 'lucide-react';

export const DocReviewPanel = ({
  docLabel, initialDate = '', dateLabel, dateHint, dateError, onDateChange, dateRequired = false, minDate, templates = [],
  approveDisabled = false, onApprove, onReject, onRejectClick, onNotApplicable, approveHint, children, approved = false,
}) => {
  const [date, setDate] = useState(initialDate || '');
  const [rejecting, setRejecting] = useState(false);
  const [selected, setSelected] = useState([]);
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);

  const run = async (fn) => { setSaving(true); try { await fn(); } finally { setSaving(false); } };
  const toggle = (t) => setSelected(p => (p.includes(t) ? p.filter(x => x !== t) : [...p, t]));
  const canReject = selected.length > 0 || reason.trim();

  return (
    <div className="rounded-lg border border-emerald-200 bg-emerald-50/40 p-3 space-y-3" data-testid="doc-decision-panel">
      <p className="text-xs font-medium text-slate-600">{approved ? 'Bu belge onaylı. Gerekirse eksik/hatalı olarak işaretleyebilirsiniz.' : 'Belgeyi inceledikten sonra kararınızı verin'}</p>
      {!approved && children}
      {!approved && dateLabel && (
        <div className="space-y-1">
          <label className="text-xs text-slate-500">{dateLabel}{dateRequired ? ' *' : ' (isteğe bağlı)'}</label>
          <input
            type="date" value={date} min={minDate} onChange={(e) => { setDate(e.target.value); onDateChange?.(e.target.value); }}
            className="h-8 w-full rounded-md border border-slate-200 bg-white px-2 text-xs"
            data-testid="doc-decision-validity"
          />
          {dateError && <p className="rounded border-2 border-rose-400 bg-rose-50 px-2 py-1 text-[11px] font-semibold text-rose-700" data-testid="doc-decision-date-error">{dateError}</p>}
          {dateHint && <p className="rounded border border-amber-200 bg-amber-50 px-2 py-1 text-[11px] text-amber-800" data-testid="doc-decision-date-hint">{dateHint}</p>}
        </div>
      )}
      {!approved && approveHint && <p className="text-[11px] text-slate-500">{approveHint}</p>}
      {rejecting ? (
        <div className="space-y-2">
          {templates.length > 0 && (
            <div className="space-y-1 max-h-32 overflow-y-auto rounded border bg-white p-2" data-testid="doc-decision-templates">
              {templates.map((t, i) => (
                <label key={i} className="flex items-start gap-2 text-xs cursor-pointer">
                  <Checkbox checked={selected.includes(t)} onCheckedChange={() => toggle(t)} data-testid={`doc-decision-template-${i}`} />
                  <span className="text-slate-600">{t}</span>
                </label>
              ))}
            </div>
          )}
          <Textarea value={reason} onChange={(e) => setReason(e.target.value)} placeholder={templates.length ? 'Ek açıklama (isteğe bağlı)...' : `${docLabel} için eksiklik/hata açıklamasını yazın...`} rows={2} className="bg-white text-xs" data-testid="doc-decision-reason" />
          <div className="flex gap-2">
            <Button size="sm" variant="ghost" className="flex-1" onClick={() => setRejecting(false)} data-testid="doc-decision-reject-cancel">Vazgeç</Button>
            <Button size="sm" onClick={() => run(() => onReject({ deficiencies: selected, reason: reason.trim() }))} disabled={saving || !canReject} className="flex-1 bg-rose-600 hover:bg-rose-700" data-testid="doc-decision-reject-confirm">Eksik/Hatalı Olarak Kaydet</Button>
          </div>
        </div>
      ) : (
        <div className="flex gap-2">
          {!approved && <Button size="sm" onClick={() => run(() => onApprove(date))} disabled={saving || approveDisabled || !!dateError || (dateRequired && !date)} className="flex-1 bg-emerald-600 hover:bg-emerald-700" data-testid="doc-decision-approve"><CheckCircle2 className="h-3.5 w-3.5 mr-1" />Onay</Button>}
          <Button size="sm" variant="outline" onClick={() => (onRejectClick ? onRejectClick() : setRejecting(true))} disabled={saving} className="flex-1 text-rose-600 border-rose-200 bg-white" data-testid="doc-decision-reject"><XCircle className="h-3.5 w-3.5 mr-1" />Eksik/Hatalı</Button>
        </div>
      )}
      {!rejecting && onNotApplicable && (
        <Button size="sm" variant="outline" onClick={() => run(onNotApplicable)} disabled={saving} className="w-full border-slate-300 bg-white text-slate-600" data-testid="doc-decision-not-applicable"><MinusCircle className="h-3.5 w-3.5 mr-1" />Kapsam Dışı</Button>
      )}
    </div>
  );
};
